import { useParams, Link } from 'react-router-dom';
import { announcementApi } from '../../api/client';
import { useApiQuery } from '../../hooks/useApiQuery';
import { Card, CardBody, ErrorAlert } from '../../components/ui';
import { ArrowLeft, Megaphone } from 'lucide-react';

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

export function AnnouncementDetailPage() {
  const { id } = useParams<{ id: string }>();

  const { data, isLoading, isError, error, refetch } = useApiQuery({
    queryKey: ['announcement', id],
    queryFn: () => announcementApi.get(Number(id)),
    enabled: !!id,
  });

  const announcement = data;

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="max-w-7xl mx-auto space-y-4">
        <Link
          to="/announcements"
          className="inline-flex items-center text-sm text-gray-600 hover:text-primary-600 transition-colors"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back to Announcements
        </Link>
        <ErrorAlert message={error?.message} onRetry={() => refetch()} />
      </div>
    );
  }

  if (!announcement) {
    return (
      <div className="max-w-7xl mx-auto space-y-4">
        <Link
          to="/announcements"
          className="inline-flex items-center text-sm text-gray-600 hover:text-primary-600 transition-colors"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back to Announcements
        </Link>
        <Card>
          <CardBody className="py-12 text-center">
            <Megaphone className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <p className="text-gray-500">Announcement not found.</p>
          </CardBody>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-4">
      <Link
        to="/announcements"
        className="inline-flex items-center text-sm text-gray-600 hover:text-primary-600 transition-colors"
      >
        <ArrowLeft className="w-4 h-4 mr-1" />
        Back to Announcements
      </Link>

      <Card>
        <CardBody className="p-6 md:p-8">
          <div className="mb-6 pb-6 border-b border-gray-200">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-9 h-9 rounded-full bg-primary-50 flex items-center justify-center">
                <Megaphone className="w-4 h-4 text-primary-600" />
              </div>
              <span className="text-xs font-medium text-primary-600 uppercase tracking-wide">Announcement</span>
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
              {announcement.title}
            </h1>
            <p className="text-sm text-gray-400">{formatDate(announcement.created_at)}</p>
          </div>

          <div
            className="prose prose-primary max-w-none prose-headings:mt-6 prose-headings:mb-3 prose-p:my-3 prose-ul:my-3 prose-ol:my-3 prose-li:my-1 prose-img:rounded-lg prose-img:shadow-md"
            dangerouslySetInnerHTML={{ __html: announcement.content }}
          />
        </CardBody>
      </Card>
    </div>
  );
}
